
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

// Get origin and redirect URL
export const getRedirectInfo = () => {
  // Use the current window location as the base URL
  const origin = window.location.origin;
  
  // Create a properly formed redirect URL that works in all environments
  // Make sure to use `/auth/callback` as the path
  const redirectTo = `${origin}/auth/callback`;
  
  console.log("Current origin:", origin);
  console.log("Configured redirect URL:", redirectTo);
  
  return { origin, redirectTo };
};

// Map authentication errors to user-friendly messages
export const getAuthErrorMessage = (error: any): string => {
  const errorMessage = error?.message || "";
  
  // Email confirmation errors
  if (errorMessage.includes("Email not confirmed")) {
    return "Your email address has not been verified. Please check your inbox and spam folder for the confirmation email.";
  }
  
  // Invalid credentials
  if (errorMessage.includes("Invalid login credentials")) {
    return "The email or password you entered is incorrect. Please try again.";
  }
  
  // Email already in use
  if (errorMessage.includes("already registered")) {
    return "This email is already registered. Please try signing in instead.";
  }
  
  // Password requirements
  if (errorMessage.includes("password")) {
    if (errorMessage.includes("length")) {
      return "Password must be at least 6 characters long.";
    }
    return "There was an issue with your password. Please ensure it meets the requirements.";
  }
  
  // Network/server errors
  if (errorMessage.includes("network") || errorMessage.includes("timeout") || errorMessage.includes("request failed")) {
    return "Unable to connect to the authentication service. Please check your internet connection and try again.";
  }
  
  // Rate limiting
  if (errorMessage.includes("rate limit") || errorMessage.includes("too many requests")) {
    return "Too many login attempts. Please try again later.";
  }
  
  // Default fallback message
  return errorMessage || "An unexpected authentication error occurred. Please try again.";
};

// Resend confirmation email with proper redirect URL
export const resendConfirmationEmail = async (email: string) => {
  if (!email) {
    toast.error("Please enter your email address");
    return false;
  }
  
  try {
    const { redirectTo } = getRedirectInfo();
    console.log("Sending confirmation email with redirect URL:", redirectTo);
    
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: redirectTo
      }
    });
    
    if (error) throw error;
    
    toast.success("Confirmation email resent! Please check your inbox AND spam folder.");
    return true;
  } catch (error: any) {
    console.error("Failed to resend confirmation email:", error);
    toast.error(getAuthErrorMessage(error));
    return false;
  }
};

// Handle manual verification
export const attemptManualVerification = async (email: string, password: string) => {
  try {
    console.log("Attempting manual verification for:", email);
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      console.error("Manual verification error:", error.message);
      if (error.message.includes("Email not confirmed")) {
        toast.error("Your account is still pending verification. Please check your email including spam folder.");
        return { success: false, requiresConfirmation: true };
      } else {
        throw error;
      }
    } else {
      toast.success("Signed in successfully!");
      return { success: true, requiresConfirmation: false };
    }
  } catch (error: any) {
    console.error("Manual verification failed:", error);
    toast.error(getAuthErrorMessage(error));
    return { success: false, requiresConfirmation: false };
  }
};

// Handle forgot password
export const handleForgotPassword = async (email: string) => {
  if (!email) {
    toast.error("Please enter your email address");
    return false;
  }

  try {
    const { origin } = getRedirectInfo();
    console.log("Sending password reset to:", email);
    console.log("Using redirect URL:", `${origin}/auth/callback`);
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${origin}/auth/callback`,
    });

    if (error) throw error;
    
    toast.success("Password reset email sent! Please check your inbox and spam folder.");
    return true;
  } catch (error: any) {
    console.error("Failed to send password reset email:", error);
    toast.error(getAuthErrorMessage(error));
    return false;
  }
};

// Configure session expiry
export const configureSessionExpiry = async (stayLoggedIn: boolean) => {
  if (!stayLoggedIn) {
    // Default session length is already long, only need to shorten it if not staying logged in
    await supabase.auth.setSession({
      access_token: (await supabase.auth.getSession()).data.session?.access_token || '',
      refresh_token: (await supabase.auth.getSession()).data.session?.refresh_token || '',
    });
  }
};
